import { createSlice } from '@reduxjs/toolkit';

import { InputSliceState, Select, Selects, SetActiveAction } from './inputSliceTypes';

import { RootState } from 'rootReducer';

import generateRandomId from 'helpers/generateRandomId';
import { getFormattedEventTime } from '../match/helpers/calculateTime';
import handleRejected from 'helpers/handleRejected';
import { editEvent } from './thunks';
import elementId from '../../constants/elementId';
import { importantMatchEventTypeIds, MatchEventType } from '../../enums/matchEventType';
import { TeamOption } from '../../enums/teamOption';
import publishOrUpdateEvent from './thunks/publishOrUpdateEvent';
import { storeItemToLog } from '../log/storeItemToLog';
import { v4 as uuidv4 } from 'uuid';

const initSelect = (id?: number) => ({
  id: id ? id : generateRandomId([]),
  value: null,
});

const initialState: InputSliceState = {
  team: null,
  activeEventTypeId: MatchEventType.COMMENT,
  inputs: {
    comment: '',
    eventTime: null,
    eventTimeFocused: false,
  },
  important: false,
  selects: {
    goalScorer: initSelect(),
    assist: initSelect(),
    redCard: initSelect(),
    yellowCard: initSelect(),
    playerOff: initSelect(elementId.playerOff),
    playerOn: initSelect(elementId.playerOn),
    corner: initSelect(),
    offside: initSelect(),
    freeKick: initSelect(),
    playerOffence: initSelect(),
    playerFouled: initSelect(),
    playerPenaltyTaker: initSelect(),
    playerPenaltyKeeper: initSelect(),
  },
  checkboxes: {
    indirectAssist: false,
    ownGoal: false,
    matchSummaryEvent: false,
  },
  activeSelectId: null,
};

export const inputSlice = createSlice({
  name: 'input',
  initialState,
  reducers: {
    setActive: (state, action: SetActiveAction) => {
      const { payload } = action;
      state.team = payload.team;
      state.activeEventTypeId = payload.eventTypeId;
      if (payload.important !== undefined) {
        state.important = payload.important;
      } else {
        state.important = importantMatchEventTypeIds.includes(payload.eventTypeId);
      }
    },
    setActiveEventTypeId: (state, action) => {
      state.activeEventTypeId = action.payload;
    },
    clearActiveSelect: (state) => {
      state.activeSelectId = null;
    },
    setEventTimeFocused: (state, action) => {
      state.inputs.eventTimeFocused = action.payload;
    },
    setImportant(state, action) {
      state.important = action.payload;
    },
    setComment: (state, action) => {
      state.inputs.comment = action.payload;
    },
    setEventTime: (state, action) => {
      state.inputs.eventTime = action.payload;
    },
    clearEventTime: (state) => {
      state.inputs.eventTime = '';
    },
    setActiveSelect: (state, action) => {
      state.activeSelectId = action.payload;
    },
    setSelectValue: (state, action) => {
      let key: keyof typeof state.selects;
      for (key in state.selects) {
        const select = state.selects[key];
        if (select.id === state.activeSelectId) {
          select.value = action.payload;
        }
      }
    },
    // checkboxes
    setIndirectAssistCheckbox: (state, action) => {
      state.checkboxes.indirectAssist = action.payload;
    },
    setMatchSummaryEventCheckbox: (state, action) => {
      state.checkboxes.matchSummaryEvent = action.payload;
    },
    setOwnGoalCheckbox: (state, action) => {
      state.checkboxes.ownGoal = action.payload;
    },
    newEvent: (state) => {
      state.inputs.comment = null;
      state.important = false;
      state.inputs.eventTime = null;

      // reset selects
      Object.keys(state.selects).forEach((key: any) => {
        const selects: { [key: string]: Select } = state.selects;
        selects[key].value = null;
      });

      // reset checkboxes
      Object.keys(state.checkboxes).forEach((key: any) => {
        const checkboxes: any = state.checkboxes;
        checkboxes[key] = false;
      });

      state.activeEventTypeId = MatchEventType.COMMENT;
      state.team = null;
    },
    // load basic event data into interface
    editEventUniversal(state, action) {
      const event = action.payload;
      if (event.comment) {
        state.inputs.comment = event.comment;
      }
      state.important = event.important;
      if (event.time || event.seconds) {
        const time = event.overtime ? event.overtime + event.time : event.time;
        state.inputs.eventTime = getFormattedEventTime(time, event.seconds);
      }
    },
    setSelectYellowCard: (state, action) => {
      state.selects.yellowCard.value = action.payload;
    },
    setSelectRedCard: (state, action) => {
      state.selects.redCard.value = action.payload;
    },
    setSelectGoalScorer: (state, action) => {
      state.selects.goalScorer.value = action.payload;
    },
    setSelectAssist: (state, action) => {
      state.selects.assist.value = action.payload;
    },
    setSelectPlayerOff: (state, action) => {
      state.selects.playerOff.value = action.payload;
    },
    setSelectPlayerOn: (state, action) => {
      state.selects.playerOn.value = action.payload;
    },
    setSelectCorner: (state, action) => {
      state.selects.corner.value = action.payload;
    },
    setSelectOffside: (state, action) => {
      state.selects.offside.value = action.payload;
    },
    setSelectFreeKick: (state, action) => {
      state.selects.freeKick.value = action.payload;
    },
    setSelectPlayerOffence: (state, action) => {
      state.selects.playerOffence.value = action.payload;
    },
    setSelectPlayerFouled: (state, action) => {
      state.selects.playerFouled.value = action.payload;
    },
    setSelectPlayerPenaltyTaker: (state, action) => {
      state.selects.playerPenaltyTaker.value = action.payload;
    },
    setSelectPlayerPenaltyKeeper: (state, action) => {
      state.selects.playerPenaltyKeeper.value = action.payload;
    },
  },
  extraReducers: (builder) => {
    handleRejected(builder, [publishOrUpdateEvent, editEvent], (state, action) => {
      console.log('Error: ', action);
      const requestId = uuidv4();
      storeItemToLog('Input slice error', action, state.match?.id, requestId);
    });
  },
});

export const selectActiveEventTypeId = (state: RootState): MatchEventType | null => state.input.activeEventTypeId;
export const selectActiveTeamOption = (state: RootState): TeamOption | null => state.input.team;
export const selectInputImportant = (state: RootState): boolean => state.input.important;
export const selectComment = (state: RootState): string | null => state.input.inputs.comment;
export const selectActiveSelectId = (state: RootState): number | null => state.input.activeSelectId;
export const selectEventTime = (state: RootState): string | null => state.input.inputs.eventTime;
export const selectShowPlayerList = (state: RootState): boolean =>
  state.input.activeEventTypeId === MatchEventType.PLAYER_OFF;
export const selectEventTimeFocused = (state: RootState): boolean => state.input.inputs.eventTimeFocused;

export const selectSelects = (state: RootState): Selects => state.input.selects;

export const selectIndirectAssistCheckbox = (state: RootState): boolean => state.input.checkboxes.indirectAssist;
export const selectMatchSummaryEventCheckbox = (state: RootState): boolean => state.input.checkboxes.matchSummaryEvent;
export const selectOwnGoalCheckbox = (state: RootState): boolean => state.input.checkboxes.ownGoal;

export const selectIsActiveEventTypeIdImportant = (state: RootState): boolean => {
  return importantMatchEventTypeIds.includes(state.input.activeEventTypeId);
};

export const inputSliceActions = inputSlice.actions;

export default inputSlice.reducer;
